import {
  manuscriptContentFromPlainText,
  normalizeManuscriptContent,
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";

/*
 * Textbook keeps ownership of pedagogical meaning and placement:
 * blockId, type, sectionItemId.
 *
 * The existing Children’s Book manuscript-content contract owns rich
 * manuscript structure and inline formatting. This adapter deliberately
 * does not invent a second rich-text schema.
 *
 * Figures are excluded because Textbook already has a separate Figure
 * authority through figureAssetId.
 */

export const TEXTBOOK_RICH_MANUSCRIPT_BLOCK_TYPES =
  Object.freeze([
    "paragraph",
    "heading",
    "learning_objectives",
    "definition",
    "worked_example",
    "exercise",
    "reference",
    "note",
    "summary",
  ]);

const TEXTBOOK_RICH_MANUSCRIPT_BLOCK_TYPE_SET =
  new Set(
    TEXTBOOK_RICH_MANUSCRIPT_BLOCK_TYPES
  );

function safeObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  )
    ? value
    : {};
}

function cleanString(value) {
  return value == null
    ? ""
    : String(value).trim();
}

function exactText(value) {
  return value == null
    ? ""
    : String(value);
}

function invalidBridgeResult(message) {
  return {
    ok: false,
    value: null,
    errors: [message],
    warnings: [],
    stats: {
      blockCount: 0,
      runCount: 0,
      textCharacterCount: 0,
    },
  };
}

/*
 * Produces an in-memory Textbook rich-manuscript state.
 *
 * When manuscriptContent is omitted, existing Textbook plain text is
 * hydrated through the proven manuscript-content contract.
 *
 * When manuscriptContent is supplied, it is normalized first and the
 * legacy Textbook text field is deterministically regenerated from that
 * structured authority. The two representations therefore cannot drift
 * inside this bridge.
 *
 * The accepted Textbook Chapter Draft persistence contract now accepts
 * this same normalized manuscriptContent shape. The legacy text field remains
 * its deterministic compatibility projection.
 */
export function prepareTextbookRichManuscriptBlock(
  block,
  manuscriptContent
) {
  const source =
    safeObject(block);

  const blockId =
    cleanString(
      source.blockId
    );

  const type =
    cleanString(
      source.type
    );

  const sectionItemId =
    cleanString(
      source.sectionItemId
    );

  const sourceText =
    exactText(
      source.text
    );

  if (!blockId) {
    return invalidBridgeResult(
      "A Textbook rich-manuscript block requires a blockId."
    );
  }

  if (
    !TEXTBOOK_RICH_MANUSCRIPT_BLOCK_TYPE_SET.has(
      type
    )
  ) {
    return invalidBridgeResult(
      type === "figure"
        ? "Textbook Figure blocks remain under the existing Figure authority and cannot enter the rich-manuscript bridge."
        : "This Textbook block type is not supported by the rich-manuscript bridge."
    );
  }

  const candidateContent =
    manuscriptContent === undefined
      ? manuscriptContentFromPlainText(
          sourceText
        )
      : manuscriptContent;

  const normalized =
    normalizeManuscriptContent(
      candidateContent
    );

  if (
    !normalized.ok ||
    !normalized.content
  ) {
    return {
      ok: false,
      value: null,
      errors:
        Array.isArray(
          normalized.errors
        )
          ? normalized.errors
          : [
              "The structured manuscript content is invalid.",
            ],
      warnings:
        Array.isArray(
          normalized.warnings
        )
          ? normalized.warnings
          : [],
      stats:
        normalized.stats || {
          blockCount: 0,
          runCount: 0,
          textCharacterCount: 0,
        },
    };
  }

  const text =
    plainTextFromManuscriptContent(
      normalized.content
    );

  return {
    ok: true,

    value: {
      blockId,
      type,
      sectionItemId,
      text,
      manuscriptContent:
        normalized.content,
    },

    errors: [],

    warnings:
      Array.isArray(
        normalized.warnings
      )
        ? normalized.warnings
        : [],

    stats:
      normalized.stats,
  };
}
